import { z } from 'zod';
/**
 * The day's news.
 *
 * ⚠️ **Two sources for one shape, and the response says WHICH.** Today is
 * computed live because the day is still running; a past day is read back
 * exactly as it was judged. A reader who cannot tell them apart cannot know
 * whether a figure will still be true in an hour — and a past day that was
 * silently recomputed would be the feature failing without saying so.
 */
export const HEADLINE_TONES = ['CRITICAL', 'HIGH', 'MEDIUM', 'INFO', 'GOOD'];
export const headlineToneSchema = z.enum(HEADLINE_TONES);
export const headlineSchema = z.object({
    key: z.string(),
    kind: z.string(),
    tone: headlineToneSchema,
    headline: z.string(),
    detail: z.string().optional(),
    rank: z.number(),
    /** Where to act on it. Absent where there is nowhere to go. */
    href: z.string().optional(),
    /** At most a few, whatever the fleet's size. */
    subjects: z.array(z.string()).optional(),
});
/**
 * How this day's news came to be.
 *
 * `LIVE` — computed now, for a day that has not finished.
 * `RECORDED` — read back as it was judged at the time.
 * `NOT_RECORDED` — nobody judged that day, and it will not be judged now.
 */
export const headlineSourceSchema = z.enum(['LIVE', 'RECORDED', 'NOT_RECORDED']);
/**
 * What a note IS, optionally.
 *
 * ⚠️ **THREE values, and "General" is the ABSENCE rather than a fourth.** An
 * ordinary note with no particular role and an unclassified note are the same
 * state — somebody wrote something down — so storing `GENERAL` beside `null`
 * would be two spellings of one thing, which is the Enabled/Availability
 * duplication this platform already paid for once: two controls, one field, and
 * a screen that could show them disagreeing. It would also make every note
 * written before today render differently from a new one whose author left the
 * picker alone. The picker still OFFERS General — as the empty value.
 *
 * ⚠️ **A note typed `RESOLUTION` resolves NOTHING.** Incidents carry a real
 * status and recommendations a governed lifecycle with transitions, guards and
 * an audit trail; this is a journal entry beside a headline. The wording on the
 * page says so, because a chip reading "Resolution" next to a story about eight
 * open incidents would otherwise read as a claim that they were closed — a
 * second, ungoverned way to appear to change state, which is exactly what the
 * copilot is forbidden from doing (D12).
 *
 * Closed, as a Zod enum, so an unrecognised value is refused at the edge rather
 * than stored and rendered as itself.
 */
export const FLEET_NEWS_NOTE_TYPES = ['EXPLANATION', 'ACTION_TAKEN', 'RESOLUTION'];
export const fleetNewsNoteTypeSchema = z.enum(FLEET_NEWS_NOTE_TYPES);
/**
 * A person's note against a day.
 *
 * ⚠️ **Two kinds, one shape.** `headlineKey` null is a note about the DAY; set,
 * it is a note about one story. They are the same act — somebody writing down
 * what they know — and the difference is one nullable field rather than two
 * models, two endpoints and two of every rule about who may edit what.
 */
export const fleetNewsNoteSchema = z.object({
    id: z.string().uuid(),
    /** Null for a note about the whole day. */
    headlineKey: z.string().nullable(),
    body: z.string(),
    /** Null is "General" — see `FLEET_NEWS_NOTE_TYPES`. */
    noteType: fleetNewsNoteTypeSchema.nullable(),
    authorUserId: z.string().uuid(),
    /** Falls back to the email; never "Unknown". */
    authorName: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * ⚠️ **The story this note was written against is no longer in the day's
     * news**, which happens because TODAY is recomputed continuously: a note
     * written at 10:00 against "Incidents rose sharply" can be orphaned by 16:00
     * when the rise no longer qualifies.
     *
     * The note is still shown, separately and marked. It is the one thing on the
     * page a person wrote, and dropping it because the platform's own judgement
     * moved would be the platform deleting somebody's words to tidy up after
     * itself.
     */
    orphaned: z.boolean(),
});
export const createFleetNewsNoteSchema = z
    .object({
    /** `YYYY-MM-DD`, UTC. A note may be added to a past day. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a UTC date as YYYY-MM-DD'),
    /** Omitted or null for a note about the whole day. */
    headlineKey: z.string().min(1).max(120).nullable().optional(),
    /*
     * Bounded like an incident note. A journal entry is a paragraph, not a
     * document — and an unbounded text column on a tenant-writable endpoint is
     * a way to fill a database.
     */
    body: z.string().trim().min(1).max(2000),
    /** Omitted or null for a General note. */
    noteType: fleetNewsNoteTypeSchema.nullable().optional(),
})
    .strict();
export const updateFleetNewsNoteSchema = z
    .object({
    body: z.string().trim().min(1).max(2000),
    /**
     * ⚠️ **Omitted is UNCHANGED; an explicit `null` CLEARS it back to General.**
     * Collapsing the two would retype somebody's note to General whenever they
     * corrected a typo — the distinction the AI allowance already draws for the
     * same reason, and the one a client that only ever sends `body` depends on.
     */
    noteType: fleetNewsNoteTypeSchema.nullable().optional(),
})
    .strict();
export const dailyHeadlinesResponseSchema = z.object({
    /** The UTC day being shown, `YYYY-MM-DD`. */
    date: z.string(),
    source: headlineSourceSchema,
    /**
     * Empty for `NOT_RECORDED`.
     *
     * ⚠️ **A day nobody recorded is NOT recomputed to fill the page.** The whole
     * point of storing these is that a past day shows what was thought at the
     * time; deriving one now from a backlog that has since been worked would
     * present today's state as Tuesday's news. The page says the day was never
     * recorded instead, which is a fact about the platform rather than about the
     * fleet.
     */
    headlines: z.array(headlineSchema),
    /** When a recorded day was written. Null for a live one. */
    recordedAt: z.string().datetime().nullable(),
    /**
     * Days in the window that carry recorded news, newest first.
     *
     * ⚠️ Sent with every response so the date navigation never offers a day that
     * turns out blank — the reader finds out before clicking rather than after.
     * Today is not in this list; it is always available and always live.
     */
    recordedDays: z.array(z.string()),
    /** How far back the page may look. */
    retentionDays: z.number().int(),
    /**
     * Notes for this day, oldest first — a journal reads forwards.
     *
     * ⚠️ Returned WITH the day rather than from an endpoint of their own: a note
     * has no meaning apart from the day it is about, and a second request would
     * let the two arrive out of step, showing yesterday's notes under today's
     * headlines for as long as one was in flight.
     */
    notes: z.array(fleetNewsNoteSchema),
    /** True where the reader may write one. */
    mayAddNote: z.boolean(),
});
export const dailyHeadlinesQuerySchema = z
    .object({
    /**
     * `YYYY-MM-DD`, UTC. Omitted means today.
     *
     * A date rather than an offset: an offset would mean something different
     * depending on when the request was made, and a link somebody shared
     * yesterday would open a different day today.
     */
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a UTC date as YYYY-MM-DD')
        .optional(),
})
    .strict();
//# sourceMappingURL=headlines.js.map